/**
 * Diff utilities for text enhancement
 * Provides context-aware diff calculations
 */

export type ContextDiffResult = {
  originalContext: string
  modifiedContext: string
  startLine: number
  endLine: number
  totalLines: number
  hasLinesBefore: boolean
  hasLinesAfter: boolean
  linesBefore: number
  linesAfter: number
}

export type ChangedLinesResult = {
  changedOriginalLines: Set<number>
  changedModifiedLines: Set<number>
}

/**
 * Calculate context-only diff showing only changed lines plus surrounding context.
 * This makes it easier to see what changed without showing the entire document.
 *
 * @param original - Original text
 * @param modified - Modified text
 * @param contextLines - Number of context lines to show before/after changes (default: 3)
 * @returns Context diff result with line numbers and indicators
 */
export function calculateContextDiff(
  original: string,
  modified: string,
  contextLines: number = 3
): ContextDiffResult {
  const originalLines = original.split("\n")
  const modifiedLines = modified.split("\n")
  const totalLines = Math.max(originalLines.length, modifiedLines.length)

  // Find which lines differ
  const changedLineIndices: number[] = []
  const maxLength = Math.max(originalLines.length, modifiedLines.length)

  for (let i = 0; i < maxLength; i++) {
    const origLine = originalLines[i] || ""
    const modLine = modifiedLines[i] || ""
    if (origLine !== modLine) {
      changedLineIndices.push(i)
    }
  }

  // If no changes found (shouldn't happen), show everything
  if (changedLineIndices.length === 0) {
    return {
      originalContext: original,
      modifiedContext: modified,
      startLine: 1,
      endLine: totalLines,
      totalLines,
      hasLinesBefore: false,
      hasLinesAfter: false,
      linesBefore: 0,
      linesAfter: 0,
    }
  }

  // Calculate range with context
  const firstChanged = changedLineIndices[0]
  const lastChanged = changedLineIndices[changedLineIndices.length - 1]

  const startLine = Math.max(0, firstChanged - contextLines)
  const endLine = Math.min(maxLength - 1, lastChanged + contextLines)

  // Extract context
  const originalContext = originalLines.slice(startLine, endLine + 1).join("\n")
  const modifiedContext = modifiedLines.slice(startLine, endLine + 1).join("\n")

  return {
    originalContext,
    modifiedContext,
    startLine: startLine + 1, // Convert to 1-based
    endLine: endLine + 1, // Convert to 1-based
    totalLines,
    hasLinesBefore: startLine > 0,
    hasLinesAfter: endLine < maxLength - 1,
    linesBefore: startLine,
    linesAfter: maxLength - 1 - endLine,
  }
}

/**
 * Identify which specific lines are changed within the context strings.
 * This enables precise line-by-line highlighting in the diff preview.
 *
 * @param originalContext - Original context string
 * @param modifiedContext - Modified context string
 * @returns Sets of line indices (0-based) that are changed in each context
 */
export function identifyChangedLines(
  originalContext: string,
  modifiedContext: string
): ChangedLinesResult {
  const originalLines = originalContext.split("\n")
  const modifiedLines = modifiedContext.split("\n")

  const changedOriginalLines = new Set<number>()
  const changedModifiedLines = new Set<number>()

  const maxLength = Math.max(originalLines.length, modifiedLines.length)

  for (let i = 0; i < maxLength; i++) {
    const origLine = originalLines[i] || ""
    const modLine = modifiedLines[i] || ""

    if (origLine !== modLine) {
      // Mark both lines as changed
      if (i < originalLines.length) {
        changedOriginalLines.add(i)
      }
      if (i < modifiedLines.length) {
        changedModifiedLines.add(i)
      }
    }
  }

  return {
    changedOriginalLines,
    changedModifiedLines,
  }
}

/**
 * Extract a meaningful section name from markdown text for display in headers.
 * Looks for the first heading or uses the first line as a fallback.
 *
 * @param markdown - Markdown text to extract section name from
 * @returns Section name (truncated if necessary)
 */
export function extractSectionName(markdown: string): string {
  if (!markdown) return "Selection"

  // Look for first heading (# Header)
  const headingMatch = markdown.match(/^#+\s+(.+)$/m)
  if (headingMatch) {
    const heading = headingMatch[1].trim()
    return heading.length > 50 ? heading.substring(0, 47) + "..." : heading
  }

  // Fallback: use first non-empty line
  const lines = markdown.split("\n").filter((line) => line.trim().length > 0)
  if (lines.length > 0) {
    const firstLine = lines[0].trim()
    // Remove markdown formatting for display
    const cleanLine = firstLine
      .replace(/^#+\s+/, "") // Headers
      .replace(/^\s*[-*+]\s+/, "") // List bullets
      .replace(/^\s*\d+\.\s+/, "") // Numbered lists
      .replace(/\*\*(.+?)\*\*/g, "$1") // Bold
      .replace(/\*(.+?)\*/g, "$1") // Italic
      .replace(/`(.+?)`/g, "$1") // Code
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // Links

    return cleanLine.length > 50 ? cleanLine.substring(0, 47) + "..." : cleanLine
  }

  return "Selection"
}
